import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { telephonyApiUrl } from '../../utils/telephony';
import i18n from '../../i18n';
import { isNativeApp } from '../../utils/native';
import { connectMobileLine, registerPhone, waitForIce } from '../../utils/mobileLine';
import {
  cancelCallNotification, getPushRegistration, onPhoneEvent, pushFields, requestMicrophonePermission, requestNotificationPermission,
  setCallAudio, setRingback, takeCallAction, takeDeclined,
} from '../../utils/nativePhone';

/**
 * In-browser phone (WebRTC) for the Netgsm santral.
 *
 * The user's tab registers as their own extension over Netsantral's WSS
 * endpoint with JsSIP, so calls ring and are answered inside Runvya. JsSIP
 * is only loaded when the backend says this user has a softphone
 * (extension + extension password + SIP server configured in Ayarlar).
 *
 * In the iOS / Android app the extension is NOT registered from the phone:
 * the voice agent keeps it registered and relays incoming calls over its
 * websocket (utils/mobileLine), so calls ring even while the app is closed.
 * The app answers with its own RTCPeerConnection; JsSIP only connects
 * (without REGISTER) to place outgoing calls. Netsantral keeps a single
 * registration per extension, so once the user's phone rings for it
 * (config.relay) the browser works the same way instead of registering.
 */
const SoftphoneContext = createContext(null);

export const useSoftphone = () => useContext(SoftphoneContext);

const PC_CONFIG = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
const MEDIA = { audio: true, video: false };

const NATIVE = isNativeApp();
const relayed = (cfg) => NATIVE || !!(cfg && cfg.relay);
const appVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const digitsOnly = (v) => String(v || '').replace(/\D/g, '');

/** Built-in ringtones: [frequency Hz, start offset s, duration s] + repeat. */
export const RINGTONES = {
  klasik: { labelKey: 'shell.softphone.ringtones.klasik', every: 3000, notes: [[880, 0, 0.35], [660, 0.4, 0.35], [880, 0.8, 0.35], [660, 1.2, 0.35]] },
  telefon: { labelKey: 'shell.softphone.ringtones.telefon', every: 3000, notes: [[480, 0, 1.2], [620, 0, 1.2]] },
  dijital: { labelKey: 'shell.softphone.ringtones.dijital', every: 1500, notes: [[1200, 0, 0.1], [1200, 0.15, 0.1], [1200, 0.3, 0.1]] },
  yumusak: { labelKey: 'shell.softphone.ringtones.yumusak', every: 3500, notes: [[523, 0, 0.5], [659, 0.5, 0.5], [784, 1.0, 0.7]] },
};
export const DEFAULT_RINGTONE = 'klasik';
const RINGTONE_KEY = 'runvya.softphone.ringtone';

const readRingtone = () => {
  try {
    const v = window.localStorage.getItem(RINGTONE_KEY);
    return RINGTONES[v] ? v : DEFAULT_RINGTONE;
  } catch (e) {
    return DEFAULT_RINGTONE;
  }
};

/** JsSIP failure causes -> what the user should know (i18n keys). */
const CAUSE_TEXT = {
  'User Denied Media Access': 'shell.softphone.causes.mediaDenied',
  Busy: 'shell.softphone.causes.busy',
  Rejected: 'shell.softphone.causes.rejected',
  'Not Found': 'shell.softphone.causes.notFound',
  Unavailable: 'shell.softphone.causes.unavailable',
  'Address Incomplete': 'shell.softphone.causes.addressIncomplete',
  'Authentication Error': 'shell.softphone.causes.authError',
  'Request Timeout': 'shell.softphone.causes.timeout',
  'Connection Error': 'shell.softphone.causes.connectionError',
  'Incompatible SDP': 'shell.softphone.causes.sdpMismatch',
  'Bad Media Description': 'shell.softphone.causes.sdpMismatch',
  'WebRTC Error': 'shell.softphone.causes.webrtcError',
  'No Answer': 'shell.softphone.causes.noAnswer',
  'SIP Failure Code': 'shell.softphone.causes.sipFailure',
};
/**
 * SIP target for a dialled string: extensions (≤6 digits) as-is; external
 * numbers as 0XXXXXXXXXX with the santral's outbound prefix in front
 * (Netsantral "Giden Arama Ayarları" Ön Ek, e.g. 35 -> 3505321234567).
 */
/** A Turkish number in a recognisable form -> 0XXXXXXXXXX, otherwise null. */
const toNational = (digits) => {
  if (digits.length === 10 && digits[0] !== '0') return `0${digits}`;
  if (digits.length === 11 && digits[0] === '0' && digits[1] !== '0') return digits;
  if (digits.length === 12 && digits.startsWith('90')) return `0${digits.slice(2)}`;
  if (digits.length === 14 && digits.startsWith('0090')) return `0${digits.slice(4)}`;
  return null;
};

/**
 * What to send to the PBX. Extensions go as-is; recognisable phone numbers
 * are normalised and get the outbound prefix (Netsantral: 35 -> trunk); a
 * number the user already typed with the prefix is not prefixed twice, and
 * anything else is dialled exactly as typed (Engin typed "35..." by hand and
 * the old normaliser cut it back to 0533..., which the PBX answers with 404).
 */
export const buildDialTarget = (phone, outboundPrefix = '') => {
  const raw = digitsOnly(phone);
  if (!raw) return '';
  if (raw.length <= 6) return raw;
  const prefix = digitsOnly(outboundPrefix);
  if (prefix && raw.startsWith(prefix)) {
    const rest = toNational(raw.slice(prefix.length));
    if (rest) return `${prefix}${rest}`;
  }
  const national = toNational(raw);
  return national ? `${prefix}${national}` : raw;
};

/** Number shown on the call card. */
export const displayNumber = (phone) => {
  const raw = digitsOnly(phone);
  return raw.length <= 6 ? raw : (toNational(raw) || raw);
};

export const causeText = (cause) => (CAUSE_TEXT[cause]
  ? i18n.t(CAUSE_TEXT[cause])
  : i18n.t('shell.softphone.causes.other', { cause }));
const QUIET_CAUSES = ['Terminated', 'Canceled', 'BYE'];

/** Netsantral expects external numbers as 0XXXXXXXXXX. */
export const toDialable = (phone) => {
  const d = digitsOnly(phone);
  const last10 = d.length >= 10 ? d.slice(-10) : d;
  return last10.length === 10 ? `0${last10}` : last10;
};

/**
 * Ringtone via Web Audio. Browsers keep an AudioContext "suspended" until the
 * user interacts with the page, so a context created when a call arrives is
 * silent (Engin: "arama sesi gelmedi"). The context is therefore created and
 * resumed on the first click/keypress (unlock), then reused for ringing.
 */
/**
 * Call history status of a call this device handled: answered, or why not.
 * Incoming: the user declining is "declined", else "missed". Outgoing: busy /
 * rejected is "declined", no answer (or the user giving up) "missed", any
 * other failure "failed".
 */
export function historyStatus(direction, answered, byUser, cause) {
  if (answered) return 'answered';
  if (direction === 'in') return byUser ? 'declined' : 'missed';
  if (['Busy', 'Rejected'].includes(cause)) return 'declined';
  if (byUser || ['Canceled', 'No Answer', 'Request Timeout', 'Unavailable'].includes(cause)) return 'missed';
  return 'failed';
}

/** "Çalıyor" tone while the callee rings: 450 Hz, 2 s on / 4 s off (Türkiye). */
export const RINGBACK = { every: 6000, notes: [[450, 0, 2]] };

/**
 * Is the other side already sending audible audio (early media, e.g. the
 * PBX's own ringback)? Then our local ringback must stop. Reads the
 * WebRTC inbound audio energy / level.
 */
export async function hearsRemoteAudio(pc) {
  if (!pc || typeof pc.getStats !== 'function') return false;
  const stats = await pc.getStats();
  let heard = false;
  stats.forEach((r) => {
    if (r.type === 'inbound-rtp' && (r.kind === 'audio' || r.mediaType === 'audio')
      && ((r.totalAudioEnergy || 0) > 0.005 || (r.audioLevel || 0) > 0.02)) {
      heard = true;
    }
  });
  return heard;
}

export function createRinger(getTone = () => RINGTONES[DEFAULT_RINGTONE]) {
  let ctx = null;
  let timer = null;
  const ensure = () => {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    }
    return ctx;
  };
  const tone = (freq, at, dur) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
    gain.gain.setValueAtTime(0.35, at + dur - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(at);
    osc.stop(at + dur);
  };
  const ring = (spec) => {
    try {
      if (!ensure()) return;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const t = ctx.currentTime + 0.01;
      (spec || getTone()).notes.forEach(([freq, at, dur]) => tone(freq, t + at, dur));
    } catch (e) {
      // audio not available — visual ring only
    }
  };
  return {
    /** Call from a user gesture: makes later rings audible. */
    unlock() {
      try {
        if (!ensure()) return false;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        return true;
      } catch (e) {
        return false;
      }
    },
    isUnlocked() {
      return Boolean(ctx && ctx.state === 'running');
    },
    /** Ring until stop(); `spec` overrides the ringtone (e.g. RINGBACK). */
    start(spec) {
      if (timer) return;
      ring(spec);
      timer = setInterval(() => ring(spec), (spec || getTone()).every);
    },
    /** One cycle of a tone (settings preview). */
    preview(name) {
      ring(RINGTONES[name] || getTone());
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
    },
  };
}

export function SoftphoneProvider({ children }) {
  const [status, setStatus] = useState('off'); // off | connecting | registered | failed
  const [config, setConfig] = useState(null);
  const [call, setCall] = useState(null); // { direction, number, name, partyUrl, state, muted, startedAt }
  const [error, setError] = useState('');
  const [audioReady, setAudioReady] = useState(false);
  const [ringtone, setRingtoneState] = useState(readRingtone);
  const ringtoneRef = useRef(ringtone);
  const userEndedRef = useRef(false);
  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const ringerRef = useRef(null);
  const lineRef = useRef(null); // mobile app: gateway connection
  const mobileRef = useRef(null); // mobile app call: { callId, offer, pc, stream }
  const answerMobileRef = useRef(null);
  const pendingAnswerRef = useRef(null); // "Cevapla" tapped on the notification
  const declinedRef = useRef(null); // "Reddet" tapped while the app was closed

  if (!ringerRef.current) ringerRef.current = createRinger(() => RINGTONES[ringtoneRef.current] || RINGTONES[DEFAULT_RINGTONE]);

  const attachRemoteAudio = useCallback((session) => {
    const pc = session && session.connection;
    if (!pc || !audioRef.current) return;
    const play = (stream) => {
      if (stream && audioRef.current.srcObject !== stream) {
        audioRef.current.srcObject = stream;
        audioRef.current.play().catch(() => {});
      }
    };
    const receivers = pc.getReceivers ? pc.getReceivers() : [];
    if (receivers.length) {
      const stream = new MediaStream();
      receivers.forEach((r) => r.track && stream.addTrack(r.track));
      play(stream);
    }
    pc.addEventListener('track', (e) => play(e.streams && e.streams[0]));
  }, []);

  const lookup = useCallback(async (number) => {
    try {
      const res = await axios.get(`${telephonyApiUrl}/api/telephony/lookup`, { params: { phone: number } });
      setCall((c) => (c && c.number === number
        ? { ...c, name: res.data.party_name || null, partyUrl: res.data.party_url || null }
        : c));
    } catch (e) {
      // unknown caller is fine
    }
  }, []);

  // Outgoing calls: our own "çalıyor" tone while the callee rings, unless
  // the PBX sends audible early media. App: native tone on the call audio
  // route (Web Audio would come out of the loudspeaker); browser: Web Audio.
  const ringbackRef = useRef(null);
  const stopRingback = useCallback(() => {
    const rb = ringbackRef.current;
    if (!rb) return;
    ringbackRef.current = null;
    clearInterval(rb.poll);
    if (NATIVE) setRingback(false);
    else ringerRef.current.stop();
  }, []);
  const startRingback = useCallback((session) => {
    if (ringbackRef.current) return;
    if (NATIVE) setRingback(true);
    else ringerRef.current.start(RINGBACK);
    const poll = setInterval(() => {
      hearsRemoteAudio(session.connection)
        .then((heard) => { if (heard) stopRingback(); })
        .catch(() => {});
    }, 500);
    ringbackRef.current = { poll };
  }, [stopRingback]);

  // Calls this device handled itself go to the user's call history (calls
  // relayed by the voice agent are written there).
  const reportCall = useCallback((entry) => {
    if (!entry.number) return;
    axios.post(`${telephonyApiUrl}/api/telephony/my-calls`, entry).catch(() => {});
  }, []);

  const bindSession = useCallback((session, direction, number) => {
    const startedIso = new Date().toISOString();
    let answeredAt = null;
    sessionRef.current = session;
    setCall({ direction, number, name: null, partyUrl: null,
      state: direction === 'in' ? 'ringing' : 'calling', muted: false, startedAt: null });
    lookup(number);

    session.on('peerconnection', () => attachRemoteAudio(session));
    // JsSIP sends the INVITE/answer only when ICE gathering completes, which
    // can take ~20 s on some networks. Go as soon as a public (srflx)
    // candidate exists, or 1.5 s after the first candidate.
    let iceTimer = null;
    session.on('icecandidate', (e) => {
      if (e.candidate && e.candidate.type === 'srflx') {
        if (iceTimer) clearTimeout(iceTimer);
        e.ready();
        return;
      }
      if (!iceTimer) iceTimer = setTimeout(() => e.ready(), 1500);
    });
    if (direction === 'out') {
      session.on('progress', (e) => { if (e.originator === 'remote') startRingback(session); });
    }
    session.on('accepted', () => {
      answeredAt = Date.now();
      stopRingback();
      ringerRef.current.stop();
      attachRemoteAudio(session);
      setCall((c) => c && { ...c, state: 'active', startedAt: Date.now() });
    });
    session.on('confirmed', () => attachRemoteAudio(session));
    const done = (e) => {
      stopRingback();
      ringerRef.current.stop();
      sessionRef.current = null;
      const byUser = userEndedRef.current;
      userEndedRef.current = false;
      reportCall({
        direction, number, started_at: startedIso,
        status: historyStatus(direction, Boolean(answeredAt), byUser, e && e.cause),
        duration: answeredAt ? Math.round((Date.now() - answeredAt) / 1000) : 0,
      });
      // Local failures matter too (e.g. microphone permission denied); only a
      // call the user ended/rejected themselves stays quiet.
      if (e && e.cause && !byUser && !QUIET_CAUSES.includes(e.cause)) {
        setError(causeText(e.cause));
      }
      setCall(null);
    };
    session.on('ended', done);
    session.on('failed', done);
  }, [attachRemoteAudio, lookup, reportCall, startRingback, stopRingback]);

  useEffect(() => {
    const unlock = () => {
      ringerRef.current.unlock();
      setTimeout(() => {
        const ok = ringerRef.current.isUnlocked();
        setAudioReady(ok);
        if (ok) EVENTS.forEach((ev) => window.removeEventListener(ev, unlock));
      }, 50);
    };
    const EVENTS = ['pointerdown', 'keydown', 'touchstart'];
    EVENTS.forEach((ev) => window.addEventListener(ev, unlock, { passive: true }));
    return () => EVENTS.forEach((ev) => window.removeEventListener(ev, unlock));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cfg;
      try {
        cfg = (await axios.get(`${telephonyApiUrl}/api/telephony/softphone`)).data;
      } catch (e) {
        return;
      }
      if (cancelled || !cfg || !cfg.enabled) return;
      setConfig(cfg);
      setStatus('connecting');
      let JsSIP;
      try {
        JsSIP = (await import('jssip')).default;
      } catch (e) {
        setStatus('failed');
        return;
      }
      if (cancelled) return;
      const socket = new JsSIP.WebSocketInterface(cfg.wss_url);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: cfg.uri,
        authorization_user: cfg.auth_user,
        password: cfg.password,
        display_name: cfg.display_name,
        // relayed lines are registered by the voice agent (see above)
        register: !relayed(cfg),
        session_timers: false,
      });
      if (relayed(cfg)) ua.on('connected', () => { setStatus('registered'); setError(''); });
      ua.on('registered', () => { setStatus('registered'); setError(''); });
      ua.on('unregistered', () => setStatus('connecting'));
      ua.on('registrationFailed', (e) => {
        setStatus('failed');
        setError(i18n.t('shell.softphone.registrationFailed', { cause: (e && e.cause) || i18n.t('shell.softphone.unknownCause') }));
      });
      ua.on('disconnected', () => setStatus((s) => (s === 'failed' ? s : 'connecting')));
      ua.on('newRTCSession', ({ session, originator }) => {
        if (originator !== 'remote') return;
        if (sessionRef.current) {
          session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); // already on a call
          return;
        }
        const caller = (session.remote_identity && session.remote_identity.uri && session.remote_identity.uri.user) || '';
        bindSession(session, 'in', caller);
        ringerRef.current.start();
      });
      ua.start();
      uaRef.current = ua;
    })();
    return () => {
      cancelled = true;
      ringerRef.current.stop();
      if (uaRef.current) {
        try { uaRef.current.stop(); } catch (e) { /* ignore */ }
        uaRef.current = null;
      }
    };
  }, [bindSession]);

  // ---- mobile app line: incoming calls relayed by the voice agent ----
  const endMobileCall = useCallback(() => {
    const m = mobileRef.current;
    mobileRef.current = null;
    if (m && m.pc) {
      try { m.pc.close(); } catch (e) { /* closed */ }
    }
    if (m && m.stream) m.stream.getTracks().forEach((t) => t.stop());
    ringerRef.current.stop();
    cancelCallNotification();
  }, []);

  const onLineMessage = useCallback((msg) => {
    if (msg.type === 'incoming') {
      if (sessionRef.current || mobileRef.current || declinedRef.current === msg.call_id) {
        lineRef.current?.send({ type: 'reject', call_id: msg.call_id }); // busy, or declined on the notification
        return;
      }
      mobileRef.current = { callId: msg.call_id, offer: msg.sdp };
      // relayed: answered through the voice agent, which also transfers it
      setCall({ direction: 'in', number: msg.from_number, name: null, partyUrl: null,
        state: 'ringing', muted: false, startedAt: null, relayed: true });
      lookup(msg.from_number);
      if (pendingAnswerRef.current === msg.call_id) {
        pendingAnswerRef.current = null;
        cancelCallNotification();
        answerMobileRef.current?.();
      } else if (!NATIVE || appVisible()) {
        // On screen: ring here. In the background the call notification
        // (CallMessagingService) keeps ringing until the app is opened.
        cancelCallNotification();
        ringerRef.current.start();
      }
    } else if (msg.type === 'transferred' && mobileRef.current?.callId === msg.call_id) {
      mobileRef.current.transfer?.done();
      endMobileCall();
      setCall(null);
    } else if (msg.type === 'transfer_failed' && mobileRef.current?.callId === msg.call_id) {
      mobileRef.current.transfer?.fail();
    } else if (msg.type === 'connected' && mobileRef.current?.callId === msg.call_id) {
      setCall((c) => c && { ...c, state: 'active', startedAt: Date.now() });
    } else if (msg.type === 'ended' && mobileRef.current?.callId === msg.call_id) {
      endMobileCall();
      if (msg.reason === 'failed') setError(i18n.t('shell.softphone.causes.webrtcError'));
      setCall(null);
    }
  }, [endMobileCall, lookup]);

  useEffect(() => {
    if (!config || !config.enabled || !relayed(config)) return undefined;
    // Register this phone for ringing, with its push token so a closed app
    // is woken (Android: FCM -> CallMessagingService -> call notification).
    const register = (fields) => registerPhone(fields || {}).catch(() => {});
    (async () => {
      await requestNotificationPermission();
      await requestMicrophonePermission(); // before the first call, not while it rings
      register(await getPushRegistration());
    })();
    const offToken = onPhoneEvent('pushToken', (data) => register(pushFields(data)));
    // Opened from the call notification: answer once the call arrives.
    const onAction = (a) => {
      // iOS: ended on the CallKit screen / a Bluetooth button during the call
      if (a && a.action === 'hangup') {
        if (mobileRef.current?.callId === a.call_id) {
          lineRef.current?.send({ type: 'hangup', call_id: a.call_id });
          endMobileCall();
          setCall(null);
        }
        return;
      }
      if (a && a.action === 'decline') {
        declinedRef.current = a.call_id;
        if (mobileRef.current?.callId === a.call_id && !mobileRef.current.pc) {
          lineRef.current?.send({ type: 'reject', call_id: a.call_id });
          endMobileCall();
          setCall(null);
        }
        return;
      }
      if (!a || a.action !== 'answer') return;
      if (mobileRef.current?.callId === a.call_id) answerMobileRef.current?.();
      else pendingAnswerRef.current = a.call_id;
    };
    takeCallAction().then(onAction);
    const offAction = onPhoneEvent('callAction', onAction);
    // Opened while a call still rings: take over from the notification.
    const onVisible = () => {
      const m = mobileRef.current;
      if (appVisible() && m && !m.pc && pendingAnswerRef.current !== m.callId) {
        cancelCallNotification();
        ringerRef.current.start();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    takeDeclined().then((id) => { if (id) declinedRef.current = id; });
    const line = connectMobileLine({ onMessage: onLineMessage });
    lineRef.current = line;
    return () => {
      offToken();
      offAction();
      document.removeEventListener('visibilitychange', onVisible);
      line.close();
      lineRef.current = null;
      endMobileCall();
    };
  }, [config, onLineMessage, endMobileCall]);

  const answerMobile = useCallback(async () => {
    const m = mobileRef.current;
    if (!m || m.pc) return;
    ringerRef.current.stop();
    setCall((c) => c && { ...c, state: 'connecting' });
    await requestMicrophonePermission();
    try {
      m.stream = await navigator.mediaDevices.getUserMedia(MEDIA);
    } catch (e) {
      lineRef.current?.send({ type: 'reject', call_id: m.callId });
      endMobileCall();
      setError(i18n.t('shell.softphone.causes.mediaDenied'));
      setCall(null);
      return;
    }
    try {
      const pc = new RTCPeerConnection(PC_CONFIG);
      m.pc = pc;
      m.stream.getTracks().forEach((t) => pc.addTrack(t, m.stream));
      pc.addEventListener('track', (e) => {
        if (audioRef.current && e.streams && e.streams[0]) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => {});
        }
      });
      await pc.setRemoteDescription({ type: 'offer', sdp: m.offer });
      await pc.setLocalDescription(await pc.createAnswer());
      await waitForIce(pc);
      if (mobileRef.current !== m) return; // the caller hung up meanwhile
      if (!lineRef.current?.send({ type: 'answer', call_id: m.callId, sdp: pc.localDescription.sdp })) {
        throw new Error('gateway offline');
      }
    } catch (e) {
      lineRef.current?.send({ type: 'hangup', call_id: m.callId });
      endMobileCall();
      setError(i18n.t('shell.softphone.causes.webrtcError'));
      setCall(null);
    }
  }, [endMobileCall]);
  answerMobileRef.current = answerMobile;

  const dial = useCallback((phone) => {
    const ua = uaRef.current;
    if (!ua || status !== 'registered' || !config) throw new Error(i18n.t('shell.softphone.notConnected'));
    if (sessionRef.current) throw new Error(i18n.t('shell.softphone.alreadyInCall'));
    const target = buildDialTarget(phone, config.outbound_prefix);
    if (!target) throw new Error(i18n.t('shell.softphone.enterNumber'));
    const number = displayNumber(phone);
    const server = config.uri.split('@')[1];
    setError('');
    const session = ua.call(`sip:${target}@${server}`, { mediaConstraints: MEDIA, pcConfig: PC_CONFIG });
    bindSession(session, 'out', number);
  }, [bindSession, config, status]);

  const answer = useCallback(() => {
    if (mobileRef.current) {
      answerMobile();
      return;
    }
    ringerRef.current.stop();
    const s = sessionRef.current;
    if (s) s.answer({ mediaConstraints: MEDIA, pcConfig: PC_CONFIG });
  }, [answerMobile]);

  const hangup = useCallback(() => {
    ringerRef.current.stop();
    if (mobileRef.current) {
      lineRef.current?.send({ type: 'hangup', call_id: mobileRef.current.callId });
      endMobileCall();
      setCall(null);
      return;
    }
    const s = sessionRef.current;
    userEndedRef.current = true;
    if (s) {
      try {
        if (s.direction === 'incoming' && !s.isEstablished()) {
          // Reject: 486 Busy Here — the caller gets a busy tone at once
          // (JsSIP's default 480 reads as "no answer", so the PBX kept ringing).
          s.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
        } else {
          s.terminate();
        }
      } catch (e) { /* already gone */ }
    }
    sessionRef.current = null;
    setCall(null);
  }, [endMobileCall]);

  const setRingtone = useCallback((name) => {
    if (!RINGTONES[name]) return;
    ringtoneRef.current = name;
    setRingtoneState(name);
    try { window.localStorage.setItem(RINGTONE_KEY, name); } catch (e) { /* private mode */ }
  }, []);

  const previewRingtone = useCallback((name) => {
    ringerRef.current.unlock();
    ringerRef.current.preview(name);
  }, []);

  const toggleMute = useCallback(() => {
    const m = mobileRef.current;
    if (m && m.stream) {
      const track = m.stream.getAudioTracks()[0];
      if (track) track.enabled = !track.enabled;
      setCall((c) => c && { ...c, muted: !!track && !track.enabled });
      return;
    }
    const s = sessionRef.current;
    if (!s) return;
    if (s.isMuted().audio) s.unmute({ audio: true }); else s.mute({ audio: true });
    setCall((c) => c && { ...c, muted: s.isMuted().audio });
  }, []);

  // App: call audio through the earpiece (or a headset) like a phone call,
  // the loudspeaker only when asked ("Hoparlör"); back to normal afterwards.
  const audioActive = useRef(false);
  const callState = call && call.state;
  const speakerOn = !!(call && call.speaker);
  useEffect(() => {
    if (!NATIVE) return;
    if (callState && callState !== 'ringing') {
      audioActive.current = true;
      setCallAudio({ active: true, speaker: speakerOn });
    } else if (!callState && audioActive.current) {
      audioActive.current = false;
      setCallAudio({ active: false });
    }
  }, [callState, speakerOn]);

  const toggleSpeaker = useCallback(() => {
    setCall((c) => c && { ...c, speaker: !c.speaker });
  }, []);

  /** Stop the ringtone without rejecting the call ("Sessize al"). */
  const silenceRing = useCallback(() => {
    ringerRef.current.stop();
    setCall((c) => c && { ...c, silenced: true });
  }, []);

  const sendDigit = useCallback((d) => {
    const sender = mobileRef.current?.pc?.getSenders?.().find((x) => x.dtmf);
    if (sender) {
      sender.dtmf.insertDTMF(d);
      return;
    }
    const s = sessionRef.current;
    if (s) s.sendDTMF(d);
  }, []);

  /**
   * Blind transfer of the current call (SIP REFER) to an extension — or an
   * external number, dialled with the outbound prefix. Our leg is hung up
   * once the PBX reports the transfer accepted; on failure the call stays.
   */
  const transfer = useCallback((target) => new Promise((resolve, reject) => {
    const dest = config ? buildDialTarget(target, config.outbound_prefix) : '';
    const m = mobileRef.current;
    if (m) {
      // Relayed call: the voice agent transfers it (SIP REFER) for us.
      if (!m.pc) return reject(new Error(i18n.t('shell.softphone.noCallToTransfer')));
      if (!dest) return reject(new Error(i18n.t('shell.softphone.enterExtension')));
      const fail = () => {
        m.transfer = null;
        setCall((c) => c && { ...c, transferring: false });
        const text = i18n.t('shell.softphone.transferFailed', { target });
        setError(text);
        reject(new Error(text));
      };
      if (!lineRef.current?.send({ type: 'transfer', call_id: m.callId, target: dest })) {
        fail();
        return undefined;
      }
      setCall((c) => c && { ...c, transferring: true });
      const timer = setTimeout(fail, 25000);
      m.transfer = {
        done: () => { clearTimeout(timer); m.transfer = null; resolve(); },
        fail: () => { clearTimeout(timer); fail(); },
      };
      return undefined;
    }
    const s = sessionRef.current;
    if (!s || !s.isEstablished()) return reject(new Error(i18n.t('shell.softphone.noCallToTransfer')));
    if (!dest) return reject(new Error(i18n.t('shell.softphone.enterExtension')));
    const server = config.uri.split('@')[1];
    const fail = (text) => {
      setCall((c) => c && { ...c, transferring: false });
      setError(text);
      reject(new Error(text));
    };
    setCall((c) => c && { ...c, transferring: true });
    s.refer(`sip:${dest}@${server}`, {
      eventHandlers: {
        requestFailed: () => fail(i18n.t('shell.softphone.transferRejected')),
        failed: () => fail(i18n.t('shell.softphone.transferFailed', { target })),
        accepted: () => {
          userEndedRef.current = true;
          try { s.terminate(); } catch (e) { /* the PBX already hung up our leg */ }
          resolve();
        },
      },
    });
    return undefined;
  }), [config]);

  const value = {
    status, config, call, error, audioReady,
    ready: status === 'registered',
    dial, answer, hangup, toggleMute, toggleSpeaker, sendDigit, silenceRing, transfer,
    canUseSpeaker: NATIVE,
    directory: (config && config.directory) || [],
    ringtone, setRingtone, previewRingtone,
    clearError: () => setError(''),
  };

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      {/* remote party audio */}
      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />
    </SoftphoneContext.Provider>
  );
}
